import type { InitFormRequest } from 'apiConnector/form/dynamic';
import { dynamicFormApiConnector } from 'apiConnector/form/dynamic';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { defaultFormReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

export interface FetchDefaultFormEpicApi {
  initForm(request: InitFormRequest): Observable<DynamicFormResult>;
}
export function fetchDefaultDynamicFormEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      selectors: { getUserPreferencesCurrency, isUserPreferencesPrivateMode, getUserPreferencesXMargin },
    },
  ) => {
    const productSelectedTabId$: Observable<InitFormRequest> = action$.pipe(
      ofType('WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED'),
      filter(action => action.workspaceType === 'dynamic'),
      withLatestFrom(state$),
      map(([action, state]) => ({
        tabId: action.tabId,
        tradeDate: getTodayStringInLocalTimezone(),
        instrumentName: action.productVariation.productType,
        instrumentContractName: action.productVariation.productSubType,
        wrapper: action.productVariation.wrapper,
        isPrivateMode: isUserPreferencesPrivateMode(state),
        currency: getUserPreferencesCurrency(state),
        selectedOnBehalfClient: action.client ?? undefined,
        xMargin: getUserPreferencesXMargin(state),
      })),
    );
    const resetTabId$: Observable<InitFormRequest> = action$.pipe(
      ofType('WORKSPACE_RESET_TAB_REQUESTED'),
      filter(action => action.workspace === 'dynamic'),
      withLatestFrom(state$),
      map(([{ tabId, productType, productSubType, wrapper, selectedOnBehalfClient }, state]) => {
        return {
          tabId,
          tradeDate: getTodayStringInLocalTimezone(),
          instrumentName: productType,
          instrumentContractName: productSubType,
          wrapper,
          selectedOnBehalfClient,
          isPrivateMode: isUserPreferencesPrivateMode(state),
          currency: getUserPreferencesCurrency(state),
          xMargin: getUserPreferencesXMargin(state),
        };
      }),
    );

    const tabId$ = merge(productSelectedTabId$, resetTabId$);

    return tabId$.pipe(
      mergeMap(request => {
        const { tabId } = request;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        const apiCall = dynamicFormApiConnector(oldHttpClient).initForm(request);

        return apiCall.pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              blocksMetadata,
              fieldsMetadata,
              productDescription,
              values,
              version,
              negotiationId,
              actions,
              quoteQuery,
              errors,
              sendWholeFormData,
              allowedRequests,
            }) =>
              of(
                defaultFormReceivedThunk(
                  tabId,
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  allowedRequests,
                  errors,
                  sendWholeFormData,
                ),
              ),
          ),
          catchError(error =>
            of(
              commonFormActions.defaultFormFailed(
                'dynamic',
                tabId,
                error.message || 'default form failed',
                error.traceId,
              ),
            ),
          ),
          timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('dynamic', tabId)), appScheduler),
        );
      }),
    );
  };
}
